import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder'
import SiteUtil from '../../shared/utils/siteUtil.js'

// Helper for formatting currency
function formatCurrency(amount, currencySymbol = '$') {
    if (amount === null || amount === undefined) return `${currencySymbol}0.00`

    return `${currencySymbol}${Number(amount).toFixed(2)}`
}

// Helper to load image from URL and convert to suitable format for receipt printer
async function loadImageForPrinter(imageUrl) {
    try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const imageType = blob.type

        console.log('Image type detected:', imageType)

        // Check if it's a supported image type
        if (!imageType.startsWith('image/')) {
            console.warn('Not a valid image type:', imageType)
            return null
        }

        // For PNG and JPEG, convert to HTMLImageElement which the encoder expects
        if (imageType === 'image/png' || imageType === 'image/jpeg' || imageType === 'image/jpg') {
            return new Promise((resolve, reject) => {
                const img = new Image()
                const objectUrl = URL.createObjectURL(blob)

                img.onload = () => {
                    URL.revokeObjectURL(objectUrl)
                    console.log('Image loaded:', img.width, 'x', img.height)
                    resolve(img)
                }

                img.onerror = (err) => {
                    URL.revokeObjectURL(objectUrl)
                    console.error('Failed to load image:', err)
                    reject(err)
                }

                img.src = objectUrl
            })
        } else {
            console.warn('Unsupported image type for receipt printer:', imageType)
            return null
        }
    } catch (error) {
        console.warn('Failed to load logo image:', error)
        return null
    }
}

// Helper to create a dashed line
function createLine(encoder, char = '-', length = 48) {
    encoder.text(char.repeat(length))
    encoder.newline()
}

// Helper to safely call encoder methods that might not exist
function safeEncoderCall(encoder, method, ...args) {
    try {
        if (typeof encoder[method] === 'function') {
            return encoder[method](...args)
        } else {
            console.warn(`Method ${method} not available on encoder`)

            return encoder
        }
    } catch (e) {
        console.warn(`Error calling encoder.${method}:`, e)

        return encoder
    }
}

// Helper to print a single kitchen item
function printKitchenItem(encoder, item, index, safeEncoderCallFn) {
    const itemName = item.menuItemName || item.label || 'Unknown Item'
    const quantity = item.quantity || 1

    safeEncoderCallFn(encoder, 'bold', true)
    safeEncoderCallFn(encoder, 'size', 'normal')
    encoder.text(`${index}. ${quantity}x ${itemName}`)
    encoder.newline()

    if (item.isWeightBased || item.is_weight_based || item.weightSold || item.weight_sold) {
        const weight = item.weightSold || item.weight_sold
        const unit = item.weightUnit || item.weight_unit || 'kg'
        const pricePerUnit = item.pricePerUnit || item.price_per_unit || 0
        encoder.text(`      ${weight} ${unit} @ $${Number(pricePerUnit).toFixed(2)}/${unit}`)
        encoder.newline()
    }

    safeEncoderCallFn(encoder, 'size', 'normal')
    safeEncoderCallFn(encoder, 'bold', false)

    if (item.orderDetailModifiers && item.orderDetailModifiers.length > 0) {
        item.orderDetailModifiers.forEach(mod => {
            const prefix = mod.appliedModType?.toLowerCase() === 'remove' ? '- ' : '+ '
            const splitIndicator = mod.splitSide === 'left' ? '[L] ' : mod.splitSide === 'right' ? '[R] ' : ''
            const displayText = mod.modifierGroupName ? `${mod.modifierGroupName}: ${mod.modifierOptionName}` : mod.modifierOptionName
            const priceText = mod.priceAdjustment > 0 ? ` (+$${Number(mod.priceAdjustment).toFixed(2)})` : ''
            encoder.text(`      ${prefix}${splitIndicator}${displayText}${priceText}`)
            encoder.newline()
        })
    }

    if (item.notes) {
        safeEncoderCallFn(encoder, 'underline', true)
        encoder.text(`   NOTE: ${item.notes}`)
        encoder.newline()
        safeEncoderCallFn(encoder, 'underline', false)
    }

    encoder.newline()
}

/**
 * Generates ESC/POS encoded data for a kitchen receipt.
 * Focuses on preparation details, modifiers, and cooking instructions.
 * @param {object} order - The full order object from POS.
 * @param {boolean} isIncremental - If true, shows only new items added
 * @param {array} newItems - Array of new items (for incremental prints)
 * @returns {Uint8Array} Encoded data to send to the kitchen printer.
 */
export function generateKitchenReceipt(order, isIncremental = false, newItems = [], isRemoval = false, removedItems = []) {
    const encoder = new ReceiptPrinterEncoder({
        characterSet: 'cp437', width: 48,
    })

    safeEncoderCall(encoder, 'initialize')
    safeEncoderCall(encoder, 'align', 'center')

    // === PRIORITY BANNER (for urgent/rush orders) ===
    const priority = order.priority || 'normal'
    if (priority === 'rush') {
        safeEncoderCall(encoder, 'bold', true)
        safeEncoderCall(encoder, 'size', 'double-height')
        safeEncoderCall(encoder, 'invert', true)
        encoder.text('  !!! RUSH ORDER !!!  ')
        encoder.newline()
        safeEncoderCall(encoder, 'invert', false)
        safeEncoderCall(encoder, 'size', 'normal')
        safeEncoderCall(encoder, 'bold', false)
        encoder.newline()
    } else if (priority === 'urgent') {
        safeEncoderCall(encoder, 'bold', true)
        safeEncoderCall(encoder, 'size', 'double-height')
        safeEncoderCall(encoder, 'invert', true)
        encoder.text('  ** URGENT **  ')
        encoder.newline()
        safeEncoderCall(encoder, 'invert', false)
        safeEncoderCall(encoder, 'size', 'normal')
        safeEncoderCall(encoder, 'bold', false)
        encoder.newline()
    }

    // === KITCHEN HEADER ===
    safeEncoderCall(encoder, 'bold', true)
    safeEncoderCall(encoder, 'size', 'double-height')
    encoder.text(isIncremental ? 'ADDITIONAL ITEMS' : 'KITCHEN ORDER')
    encoder.newline()
    safeEncoderCall(encoder, 'size', 'normal')
    safeEncoderCall(encoder, 'bold', false)

    // Order number prominently displayed
    safeEncoderCall(encoder, 'invert', true)

    const kitchenDisplayNum = order.backend_order_id || order.backendOrderId || order.orderNumber || order.id
    encoder.text(`  ORDER #${kitchenDisplayNum}  `)
    encoder.newline()
    safeEncoderCall(encoder, 'invert', false)

    encoder.newline()

    // === ORDER INFO ===
    safeEncoderCall(encoder, 'align', 'left')
    safeEncoderCall(encoder, 'bold', true)

    // Order type and timing info
    if (order.orderType) {
        const typeLabel = order.orderType === 'dine_in' ? 'DINE-IN' : order.orderType.toUpperCase()
        encoder.text(`TYPE: ${typeLabel}`)
        encoder.newline()
    }

    const orderTime = new Date(order.sentToKitchenAt || order.createdAt || Date.now()).toLocaleTimeString()

    encoder.text(`TIME: ${orderTime}`)
    encoder.newline()

    // Table number if dine-in
    if (order.tableName) {
        encoder.text(`TABLE: ${order.tableName}`)
        encoder.newline()
    }

    // Customer name if available (for pickup/delivery)
    if (order.customerName) {
        encoder.text(`CUSTOMER: ${order.customerName}`)
        encoder.newline()
    }

    safeEncoderCall(encoder, 'bold', false)
    createLine(encoder, '=')

    // === ITEMS TO PREPARE ===
    safeEncoderCall(encoder, 'bold', true)
    encoder.text(isRemoval ? 'REMOVED ITEMS FOR ORDER:' : (isIncremental ? 'NEW ITEMS:' : 'ITEMS TO PREPARE:'))
    encoder.newline()
    safeEncoderCall(encoder, 'bold', false)
    createLine(encoder)

    const itemsToPrint = isRemoval ? removedItems : (isIncremental ? newItems : (order.orderDetails || order.items || []))

    // Check if any items have courses
    const hasCourses = itemsToPrint.some(item => item.courseId || item.course_id)

    if (hasCourses) {
        // Group items by course
        const courseGroups = groupItemsByCourse(itemsToPrint)
        let itemIndex = 0

        courseGroups.forEach((group, groupIndex) => {
            // Print course header
            if (group.courseName) {
                safeEncoderCall(encoder, 'bold', true)
                safeEncoderCall(encoder, 'invert', true)
                encoder.text(`  === ${group.courseName.toUpperCase()} ===  `)
                encoder.newline()
                safeEncoderCall(encoder, 'invert', false)
                safeEncoderCall(encoder, 'bold', false)
            } else if (courseGroups.length > 1) {
                encoder.text('--- No Course ---')
                encoder.newline()
            }

            // Print items in this course
            group.items.forEach((item) => {
                itemIndex++
                printKitchenItem(encoder, item, itemIndex, safeEncoderCall)
            })

            // Add separator between course groups
            if (groupIndex < courseGroups.length - 1) {
                encoder.newline()
            }
        })
    } else {
        // No courses - print items normally
        itemsToPrint.forEach((item, index) => {
            printKitchenItem(encoder, item, index + 1, safeEncoderCall)
        })
    }

    // === SPECIAL NOTES ===
    if (order.notes || order.customerNotes) {
        createLine(encoder, '=')
        safeEncoderCall(encoder, 'bold', true)
        encoder.text('SPECIAL INSTRUCTIONS:')
        encoder.newline()
        safeEncoderCall(encoder, 'bold', false)

        const instructions = order.notes || order.customerNotes

        encoder.text(instructions)
        encoder.newline()
        encoder.newline()
    }

    // === TIMING INFO ===
    createLine(encoder, '=')
    safeEncoderCall(encoder, 'align', 'center')

    if (order.orderType === 'delivery' && order.scheduledAt) {
        safeEncoderCall(encoder, 'invert', true)
        encoder.text(`  DELIVERY: ${new Date(order.scheduledAt).toLocaleTimeString()}  `)
        encoder.newline()
        safeEncoderCall(encoder, 'invert', false)
    } else if (order.orderType === 'pickup' && order.scheduledAt) {
        safeEncoderCall(encoder, 'invert', true)
        encoder.text(`  PICKUP: ${new Date(order.scheduledAt).toLocaleTimeString()}  `)
        encoder.newline()
        safeEncoderCall(encoder, 'invert', false)
    } else if (order.orderType === 'dine_in') {
        safeEncoderCall(encoder, 'invert', true)
        encoder.text('  DINE IN - SERVE HOT  ')
        encoder.newline()
        safeEncoderCall(encoder, 'invert', false)
    }

    encoder.newline()

    // === FOOTER ===
    createLine(encoder, '=')
    safeEncoderCall(encoder, 'align', 'center')
    safeEncoderCall(encoder, 'bold', true)
    encoder.text(`ORDER #${order.backend_order_id || order.backendOrderId || order.orderNumber || order.id}`)
    encoder.newline()
    safeEncoderCall(encoder, 'bold', false)

    encoder.text(`Printed: ${new Date().toLocaleTimeString()}`)
    encoder.newline()

    // Extra spacing for paper feed
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()

    safeEncoderCall(encoder, 'cut')

    encoder.newline()
    encoder.newline()

    return encoder.encode()
}

/**
 * Generates ESC/POS encoded data for a thermal receipt.
 * @param {object} order - The full order object from your backend.
 * @param {object} config - Nuxt runtime config for building media URLs.
 * @returns {Uint8Array} Encoded data to send to the printer.
 */
export async function generateReceipt(order, config = null) {
    console.log('[DEBUG MODS] Generating receipt for order :', order)
    // Use saved tax rate from order, or fallback to currency-based rate
    let gstRate
    if (order.taxRate) {
        gstRate = order.taxRate
    } else {
        // Fallback for old orders without saved tax rate
        const currency = order.business?.currency || 'AUD'
        gstRate = currency === 'NZD' ? (config?.public?.gstNZD || 0.15) : (config?.public?.gstAUD || 0.10)
    }
    const gstDivisor = 1 + gstRate
    const gstPercentage = Number(gstRate * 100).toFixed(0)

    const encoder = new ReceiptPrinterEncoder({
        characterSet: 'cp437', width: 48,
    })

    // Initialize - some versions might not have this
    safeEncoderCall(encoder, 'initialize')

    // Center alignment for header
    safeEncoderCall(encoder, 'align', 'center')
    createLine(encoder, '=')
    // Business Logo (if available and enabled)
    const showLogo = order.business?.receiptShowLogo !== false
    if (showLogo && order.business?.logo && config) {
        try {
            const logoUrl = SiteUtil.getPictureUrl(order.business.logo, config)
            if (logoUrl) {
                const logoImage = await loadImageForPrinter(logoUrl)
                if (logoImage) {
                    try {
                        encoder.image(logoImage, 200, 200, 'atkinson')
                    } catch (imgError) {
                        console.warn('Failed to encode logo image:', imgError)
                    }
                }
            }
        } catch (error) {
            console.warn('Failed to add logo to receipt:', error)
        }
    }
    createLine(encoder, '=')
    // Tax Invoice Header (for GST compliance)
    const isGSTRegistered = order.business?.gstRegistered !== false
    if (isGSTRegistered) {
        safeEncoderCall(encoder, 'bold', true)
        safeEncoderCall(encoder, 'align', 'center')
        encoder.text('TAX INVOICE')
        encoder.newline()
        safeEncoderCall(encoder, 'bold', false)
        createLine(encoder, '=')
    }

    // --- Business Header ---
    if (order.business?.bName) {
        safeEncoderCall(encoder, 'bold', true)
        encoder.text(order.business.bName.toUpperCase())
        encoder.newline()
        safeEncoderCall(encoder, 'bold', false)
    }

    // Business Contact Details
    if (order.business?.address) {
        encoder.newline()
        encoder.text(order.business.address)
        encoder.newline()
        let location = ''
        if (order.business.suburb) location += order.business.suburb
        if (order.business.state) location += ` ${order.business.state}`
        if (order.business.postCode) location += ` ${order.business.postCode}`
        if (location) {
            encoder.text(location)
            encoder.newline()
        }
    }

    if (order.business?.phone) {
        encoder.text(`Ph: ${order.business.phone}`)
        encoder.newline()
    }

    if (order.business?.bEmail) {
        encoder.text(`Email: ${order.business.bEmail}`)
        encoder.newline()
    }

    // ABN/ACN (MANDATORY for tax compliance)
    if (order.business?.taxNumber) {
        safeEncoderCall(encoder, 'bold', true)
        const taxLabel = order.business.taxLabel || 'ABN'
        encoder.text(`${taxLabel}: ${order.business.taxNumber}`)
        encoder.newline()
        safeEncoderCall(encoder, 'bold', false)
    }

    encoder.newline()

    // --- Order Info ---
    safeEncoderCall(encoder, 'align', 'left')
    const displayOrderNum = order.backend_order_id || order.backendOrderId || order.orderNumber || `Local-${order.id?.toString().split('_')[1]?.slice(-6) || 'DRAFT'}`
    encoder.text(`Order #: ${displayOrderNum}`)
    encoder.newline()
    if (order.orderType === 'delivery' && order.deliveryProvider === 'self' && order.deliveryPin) {
        safeEncoderCall(encoder, 'align', 'left')
        encoder.text(`Driver PIN: ${order.deliveryPin}`)
        encoder.newline()
    }

    if (order.placedAt) {
        const displayPlacedAt = new Date(order.placedAt).toLocaleString()

        encoder.text(`Date: ${displayPlacedAt}`)
        encoder.newline()
    }

    if (order.orderType) {
        encoder.text(`Type: ${order.orderType.charAt(0).toUpperCase() + order.orderType.slice(1)}`)
        encoder.newline()
    }

    if (order.tableName) {
        encoder.text(`Table: ${order.tableName}`)
        encoder.newline()
    }

    if (order.serverName) {
        encoder.text(`Server: ${order.serverName}`)
        encoder.newline()
    }

    // --- Items Header ---
    createLine(encoder)
    safeEncoderCall(encoder, 'bold', true)
    encoder.text('Qty Item Name                     Price   Total')
    encoder.newline()
    safeEncoderCall(encoder, 'bold', false)
    createLine(encoder)

    // --- Items ---
    ;(order.orderDetails || []).forEach(item => {
        const itemName = item.menuItemName || 'Unknown Item'
        const quantity = item.quantity || 1
        const unitPrice = item.unitPrice || 0
        const itemTotal = item.itemSubtotal || (quantity * unitPrice)

        // Format item line carefully for alignment
        let qtyStr = quantity.toString().padEnd(4)
        let nameStr = itemName.substring(0, 28).padEnd(28)
        let priceStr = formatCurrency(unitPrice, '').padStart(7)
        let totalStr = formatCurrency(itemTotal, '').padStart(7)

        encoder.text(`${qtyStr}${nameStr} ${priceStr} ${totalStr}`)
        encoder.newline()

        // Weight-based item info
        if (item.isWeightBased || item.is_weight_based || item.weightSold || item.weight_sold) {
            const weight = item.weightSold || item.weight_sold
            const unit = item.weightUnit || item.weight_unit || 'kg'
            const pricePerUnit = item.pricePerUnit || item.price_per_unit || 0
            encoder.text(`   (${weight} ${unit} @ ${formatCurrency(pricePerUnit, '')}/${unit})`)
            encoder.newline()
        }

        // Modifiers for this item
        if (item.orderDetailModifiers && item.orderDetailModifiers.length > 0) {
            item.orderDetailModifiers.forEach(mod => {
                const modLabel = mod.modifierGroupName || mod.modifierGroupItem?.modifierGroup?.name || ''
                const modOption = mod.modifierOptionName
                const prefix = mod.appliedModType?.toLowerCase() === 'remove' ? '- ' : '+ '
                const splitIndicator = mod.splitSide === 'left' ? '[L] ' : mod.splitSide === 'right' ? '[R] ' : ''
                const displayText = modLabel ? `${modLabel}: ${modOption}` : modOption
                let modName = `${prefix}${splitIndicator}${displayText}`

                if (mod.priceAdjustment !== 0 && mod.priceAdjustment !== null) {
                    let modPriceStr = formatCurrency(mod.priceAdjustment, '').padStart(7)
                    modName = modName.substring(0, 32).padEnd(32)
                    encoder.text(`.. ${modName} ${modPriceStr}`)
                    encoder.newline()
                } else {
                    encoder.text(`.. ${modName.substring(0, 40)}`)
                    encoder.newline()
                }
            })
        }

        // Item notes (special instructions)
        if (item.notes) {
            encoder.text(`   Note: ${item.notes.substring(0, 40)}`)
            encoder.newline()
        }
    })

    createLine(encoder)

    // --- Totals ---
    safeEncoderCall(encoder, 'align', 'right')
    encoder.text(`Subtotal: ${formatCurrency(order.subtotal)}`)
    encoder.newline()

    // Deal discounts
    const appliedDeals = order.appliedDeals || order.applied_deals || []
    const dealsArray = typeof appliedDeals === 'string' ? JSON.parse(appliedDeals) : appliedDeals
    let totalDealDiscount = 0

    if (dealsArray && dealsArray.length > 0) {
        dealsArray.forEach(deal => {
            if (deal.amount > 0) {
                const dealName = deal.name || deal.dealName || 'Deal'
                encoder.text(`${dealName}: -${formatCurrency(deal.amount)}`)
                encoder.newline()
                totalDealDiscount += Number(deal.amount)
            }
        })
    }

    // Manual discount (only show if there's additional discount beyond deal discount)
    const totalDiscountAmount = Number(order.discountAmount || 0)
    const manualDiscount = totalDiscountAmount - totalDealDiscount
    if (manualDiscount > 0.01) {
        encoder.text(`Discount: -${formatCurrency(manualDiscount)}`)
        encoder.newline()
    }

    const voucherCode = order.appliedVoucherCode || order.applied_voucher_code
    const voucherDiscountAmount = order.voucherDiscount || order.voucher_discount || 0
    if (voucherCode || voucherDiscountAmount > 0) {
        if (voucherCode) {
            encoder.text(`Voucher: ${voucherCode}`)
            encoder.newline()
        }
        if (voucherDiscountAmount > 0) {
            encoder.text(`Voucher Discount: -${formatCurrency(voucherDiscountAmount)}`)
            encoder.newline()
        }
    }

    if (order.loyaltyRewardName || order.loyalty_reward_name) {
        const rewardName = order.loyaltyRewardName || order.loyalty_reward_name
        const rewardType = order.loyaltyRewardType || order.loyalty_reward_type
        const rewardValue = order.loyaltyRewardValue || order.loyalty_reward_value
        const pointsRedeemed = order.loyaltyPointsRedeemed || order.loyalty_points_redeemed
        const discountAmount = order.loyaltyRewardDiscount || order.loyalty_reward_discount || order.loyalty_discount_applied || 0

        // Format reward description with value
        let rewardDescription = rewardName
        if (rewardType === 'discount_percentage' && rewardValue) {
            rewardDescription += ` (${rewardValue}% off)`
        } else if (rewardType === 'discount_fixed' && rewardValue) {
            rewardDescription += ` ($${rewardValue} off)`
        }

        encoder.text(`Loyalty Reward: ${rewardDescription}`)
        encoder.newline()
        if (discountAmount > 0) {
            encoder.text(`Reward Discount: -${formatCurrency(discountAmount)}`)
            encoder.newline()
        }
        if (pointsRedeemed) {
            encoder.text(`Points Redeemed: ${pointsRedeemed}`)
            encoder.newline()
        }
    }

    const giftCardAmount = order.giftCardAmount || order.gift_card_amount || 0
    const giftCardCode = order.giftCardCode || order.gift_card_code
    if (giftCardAmount > 0 || giftCardCode) {
        if (giftCardCode) {
            encoder.text(`Gift Card: ${giftCardCode}`)
            encoder.newline()
        }
        if (giftCardAmount > 0) {
            encoder.text(`Gift Card Applied: -${formatCurrency(giftCardAmount)}`)
            encoder.newline()
        }
    }

    if (order.deliveryFee > 0) {
        encoder.text(`Delivery Fee: ${formatCurrency(order.deliveryFee)}`)
        encoder.newline()
    }

    if (order.serviceFee > 0) {
        encoder.text(`Service Fee: ${formatCurrency(order.serviceFee)}`)
        encoder.newline()
    }

    if (order.paymentSurcharge > 0) {
        encoder.text(`Payment Surcharge: ${formatCurrency(order.paymentSurcharge)}`)
        encoder.newline()
    }

    if (order.orderSurcharge > 0) {
        encoder.text(`Order Surcharge: ${formatCurrency(order.orderSurcharge)}`)
        encoder.newline()
    }

    if (order.tipAmount > 0) {
        encoder.text(`Tip: ${formatCurrency(order.tipAmount)}`)
        encoder.newline()
    }

    if (order.platformFeeApplied > 0) {
        encoder.text(`Platform Fee: ${formatCurrency(order.platformFeeApplied)}`)
        encoder.newline()
    }

    createLine(encoder, '-')

    // GST Breakdown (MANDATORY for tax compliance)
    const subtotalBeforeGst = order.subtotal / gstDivisor
    const gstAmount = order.taxAmount || (order.subtotal - subtotalBeforeGst)

    if (isGSTRegistered && gstAmount > 0) {
        encoder.text(`Subtotal (Ex GST): ${formatCurrency(subtotalBeforeGst)}`)
        encoder.newline()
        encoder.text(`GST (${gstPercentage}%): ${formatCurrency(gstAmount)}`)
        encoder.newline()
    } else if (order.taxAmount > 0) {
        encoder.text(`GST: ${formatCurrency(order.taxAmount)}`)
        encoder.newline()
    }

    createLine(encoder, '=')

    safeEncoderCall(encoder, 'bold', true)
    encoder.text(`TOTAL: ${formatCurrency(order.totalAmount)}`)
    encoder.newline()
    safeEncoderCall(encoder, 'bold', false)

    createLine(encoder, '=')

    // Payment Method Details - check both top level and paymentInfo
    const paymentInfo = order.paymentInfo || {}
    const cardType = order.cardType || paymentInfo.cardType
    const last4Digits = order.last4Digits || paymentInfo.last4Digits
    const terminalTransactionId = order.terminalTransactionId || paymentInfo.terminalTransactionId
    const terminalAuthCode = order.terminalAuthCode || paymentInfo.terminalAuthCode
    const terminalAid = order.terminalAid || paymentInfo.terminalAid
    const terminalProvider = order.terminalProvider || paymentInfo.terminalProvider

    if (order.paymentMethodType) {
        encoder.newline()
        safeEncoderCall(encoder, 'bold', true)
        encoder.text('PAYMENT DETAILS')
        encoder.newline()
        safeEncoderCall(encoder, 'bold', false)
        createLine(encoder, '-')

        const paymentType = order.paymentMethodType?.toUpperCase() || 'CARD'
        encoder.text(`Method: ${paymentType}`)
        encoder.newline()

        if (cardType) {
            encoder.text(`Card: ${cardType}`)
            if (last4Digits) {
                encoder.text(` ****${last4Digits}`)
            }
            encoder.newline()
        } else if (last4Digits) {
            encoder.text(`Card: ****${last4Digits}`)
            encoder.newline()
        }

        if (terminalTransactionId) {
            encoder.text(`Ref: ${terminalTransactionId}`)
            encoder.newline()
        }

        if (terminalAuthCode) {
            encoder.text(`Auth: ${terminalAuthCode}`)
            encoder.newline()
        }

        if (terminalAid) {
            encoder.text(`AID: ${terminalAid}`)
            encoder.newline()
        }

        if (terminalProvider) {
            encoder.text(`Terminal: ${terminalProvider.toUpperCase()}`)
            encoder.newline()
        }

        createLine(encoder, '-')
    }

    // Customer Information (for tax invoice requirements over $1000)
    if (isGSTRegistered && order.totalAmount >= 1000) {
        encoder.newline()
        safeEncoderCall(encoder, 'bold', true)
        encoder.text('CUSTOMER DETAILS:')
        encoder.newline()
        safeEncoderCall(encoder, 'bold', false)

        if (order.customerFirstName || order.customerLastName) {
            encoder.text(`${order.customerFirstName || ''} ${order.customerLastName || ''}`.trim())
            encoder.newline()
        }
        if (order.customerEmail) {
            encoder.text(order.customerEmail)
            encoder.newline()
        }
        if (order.customerPhone) {
            encoder.text(order.customerPhone)
            encoder.newline()
        }
    }

    // --- Footer ---
    safeEncoderCall(encoder, 'align', 'center')
    encoder.newline()
    encoder.text('Thank You for Your Business!')
    encoder.newline()
    encoder.newline()
    encoder.text('Voluntary tips does not attract GST')
    encoder.newline()

    // Custom footer text (from business settings)
    if (order.business?.receiptFooterText) {
        encoder.newline()
        createLine(encoder, '-')
        encoder.text(order.business.receiptFooterText)
        encoder.newline()
    }

    // QR Code (from business settings)
    if (order.business?.receiptShowQrCode && order.business?.receiptQrCodeUrl) {
        encoder.newline()
        createLine(encoder, '-')
        if (order.business.receiptQrCodeLabel) {
            safeEncoderCall(encoder, 'bold', true)
            encoder.text(order.business.receiptQrCodeLabel)
            encoder.newline()
            safeEncoderCall(encoder, 'bold', false)
        }
        try {
            safeEncoderCall(encoder, 'qrcode', order.business.receiptQrCodeUrl, 1, 4, 'h')
        } catch (qrError) {
            console.warn('Failed to generate QR code:', qrError)
            encoder.text('[QR Code]')
            encoder.newline()
        }
    }

    // Add multiple newlines for paper feed
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()

    // Try to cut paper if the method exists
    safeEncoderCall(encoder, 'cut')

    // Open cash drawer (ESC/POS command: ESC p m t1 t2)
    encoder.raw([0x1B, 0x70, 0x00, 0x19, 0xFA])

    return encoder.encode()
}

export function generateUpdatedItemTicket(order, updatedItem) {
    const encoder = new ReceiptPrinterEncoder({
        characterSet: 'cp437', width: 48,
    })

    safeEncoderCall(encoder, 'initialize')
    safeEncoderCall(encoder, 'align', 'center')

    safeEncoderCall(encoder, 'bold', true)
    safeEncoderCall(encoder, 'size', 'double-height')
    encoder.text('** UPDATED TICKET **')
    encoder.newline()
    safeEncoderCall(encoder, 'size', 'normal')
    safeEncoderCall(encoder, 'bold', false)

    safeEncoderCall(encoder, 'invert', true)
    const kitchenDisplayNum = order.backend_order_id || order.backendOrderId || order.orderNumber || order.id
    encoder.text(`  ORDER #${kitchenDisplayNum}  `)
    encoder.newline()
    safeEncoderCall(encoder, 'invert', false)

    encoder.newline()

    safeEncoderCall(encoder, 'align', 'left')
    safeEncoderCall(encoder, 'bold', true)

    if (order.orderType) {
        const typeLabel = order.orderType === 'dine_in' ? 'DINE-IN' : order.orderType.toUpperCase()
        encoder.text(`TYPE: ${typeLabel}`)
        encoder.newline()
    }

    const updateTime = new Date().toLocaleTimeString()
    encoder.text(`UPDATED: ${updateTime}`)
    encoder.newline()

    if (order.tableName) {
        encoder.text(`TABLE: ${order.tableName}`)
        encoder.newline()
    }

    safeEncoderCall(encoder, 'bold', false)
    createLine(encoder, '=')

    safeEncoderCall(encoder, 'bold', true)
    encoder.text('NOTES CHANGED FOR:')
    encoder.newline()
    safeEncoderCall(encoder, 'bold', false)
    createLine(encoder)

    const itemName = updatedItem.menuItemName || updatedItem.label || 'Unknown Item'
    const quantity = updatedItem.quantity || 1

    safeEncoderCall(encoder, 'bold', true)
    safeEncoderCall(encoder, 'size', 'normal')
    encoder.text(`${quantity}x ${itemName}`)
    encoder.newline()
    safeEncoderCall(encoder, 'bold', false)

    if (updatedItem.orderDetailModifiers && updatedItem.orderDetailModifiers.length > 0) {
        updatedItem.orderDetailModifiers.forEach(mod => {
            const modName = mod.modifierOptionName || mod.name || 'Modifier'
            const prefix = mod.appliedModType?.toLowerCase() === 'remove' ? 'NO ' : ''
            encoder.text(`   ${prefix}${modName}`)
            encoder.newline()
        })
    }

    if (updatedItem.notes) {
        encoder.newline()
        safeEncoderCall(encoder, 'bold', true)
        safeEncoderCall(encoder, 'invert', true)
        encoder.text(' >>> NEW NOTES: ')
        safeEncoderCall(encoder, 'invert', false)
        encoder.newline()
        safeEncoderCall(encoder, 'bold', false)
        encoder.text(`   ${updatedItem.notes}`)
        encoder.newline()
    }

    if (updatedItem.courseName) {
        encoder.newline()
        encoder.text(`COURSE: ${updatedItem.courseName}`)
        encoder.newline()
    }

    createLine(encoder, '=')
    encoder.newline()
    encoder.newline()
    encoder.newline()

    safeEncoderCall(encoder, 'cut')

    return encoder.encode()
}

// for testing printer/ save paper
export function generateMinimalReceipt(order) {
    const encoder = new ReceiptPrinterEncoder({
        characterSet: 'cp437', width: 48,
    })

    encoder.text('========== RECEIPT ==========')
    encoder.newline()
    const displayOrderNum = order.backend_order_id || order.backendOrderId || order.orderNumber || 'DRAFT'
    encoder.text(`Order #: ${displayOrderNum}`)
    encoder.newline()
    encoder.text('-----------------------------')
    encoder.newline()

    // Simple items without any formatting
    ;(order.details || []).forEach(item => {
        encoder.text(`${item.quantity}x ${item.menuItemName}`)
        encoder.newline()
        encoder.text(`   ${formatCurrency(item.itemSubtotal)}`)
        encoder.newline()
    })

    encoder.text('-----------------------------')
    encoder.newline()
    encoder.text(`TOTAL: ${formatCurrency(order.totalAmount)}`)
    encoder.newline()
    encoder.text('=============================')
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()
    encoder.newline()

    safeEncoderCall(encoder, 'cut')

    encoder.newline()
    encoder.newline()

    return encoder.encode()
}

/**
 * Generates a text-only version of the customer receipt for preview purposes.
 * @param {object} order - The full order object from your backend.
 * @param {object} config - Nuxt runtime config for building media URLs.
 * @returns {string} Plain text receipt content.
 */
export function generateReceiptText(order, config = null) {
    let content = ''

    // Use saved tax rate from order, or fallback to currency-based rate
    let gstRate
    if (order.taxRate) {
        gstRate = order.taxRate
    } else {
        // Fallback for old orders without saved tax rate
        const currency = order.business?.currency || 'AUD'
        gstRate = currency === 'NZD' ? (config?.public?.gstNZD || 0.15) : (config?.public?.gstAUD || 0.10)
    }
    const gstDivisor = 1 + gstRate
    const gstPercentage = Number(gstRate * 100).toFixed(0)

    // Business Logo placeholder in text (if available)
    if (order.business?.logo && config) {
        const logoUrl = SiteUtil.getPictureUrl(order.business.logo, config)
        if (logoUrl) {
            content += '[LOGO]'.padStart(26) + '\n'
            content += '\n'
        }
    }

    // Tax Invoice Header (for GST compliance)
    const isGSTRegistered = order.business?.gstRegistered !== false // Assume GST registered unless explicitly false
    if (isGSTRegistered && order.totalAmount >= 75) {
        content += 'TAX INVOICE'.padStart(30) + '\n'
        content += createLineText('=', 48)
    }

    // Business Header with Compliance Info
    if (order.business?.bName) {
        content += `${order.business.name.toUpperCase()}\n`
    }

    // Business Contact Details (Required for Consumer Protection)
    if (order.business?.address) {
        content += `${order.business.address}\n`
        if (order.business.suburb) content += `${order.business.suburb}`
        if (order.business.state) content += ` ${order.business.state}`
        if (order.business.postCode) content += ` ${order.business.postCode}\n`
        else content += '\n'
    }

    if (order.business?.phone) {
        content += `Ph: ${order.business.phone}\n`
    }

    if (order.business?.bEmail) {
        content += `Email: ${order.business.bEmail}\n`
    }

    // ABN/ACN (MANDATORY for tax compliance)
    if (order.business?.taxNumber) {
        const taxLabel = order.business.taxLabel || 'ABN'
        content += `${taxLabel}: ${order.business.taxNumber}\n`
    }

    content += '\n'

    // Order info
    const displayOrderNum3 = order.backend_order_id || order.backendOrderId || order.orderNumber || `Local-${order.id.split('_')[1]?.slice(-6) || 'DRAFT'}`
    content += `Order #: ${displayOrderNum3}\n`

    if (order.orderType === 'delivery' && order.deliveryProvider === 'self' && order.deliveryPin) {
        content += `Driver PIN: ${order.deliveryPin}\n`
    }

    if (order.placedAt) {
        const displayPlacedAt = new Date(order.placedAt).toLocaleString()
        content += `Date: ${displayPlacedAt}\n`
    }

    if (order.orderType) {
        content += `Type: ${order.orderType.charAt(0).toUpperCase() + order.orderType.slice(1)}\n`
    }

    if (order.tableName) {
        content += `Table: ${order.tableName}\n`
    }

    if (order.serverName) {
        content += `Server: ${order.serverName}\n`
    }

    // Items header
    content += createLineText()
    content += 'Qty Item Name                 Price   Total\n'
    content += createLineText()

    // Items
    ;(order.orderDetails || []).forEach(item => {
        const itemName = item.menuItemName || 'Unknown Item'
        const quantity = item.quantity || 1
        const unitPrice = item.unitPrice || 0
        const itemTotal = item.itemSubtotal || (quantity * unitPrice)

        // Format item line carefully for alignment
        const qtyStr = quantity.toString().padEnd(4)
        const nameStr = itemName.substring(0, 28).padEnd(28)
        const priceStr = formatCurrency(unitPrice, '').padStart(7)
        const totalStr = formatCurrency(itemTotal, '').padStart(7)

        content += `${qtyStr}${nameStr} ${priceStr} ${totalStr}\n`

        // Weight-based item info
        if (item.isWeightBased || item.is_weight_based || item.weightSold || item.weight_sold) {
            const weight = item.weightSold || item.weight_sold
            const unit = item.weightUnit || item.weight_unit || 'kg'
            const pricePerUnit = item.pricePerUnit || item.price_per_unit || 0
            content += `   (${weight} ${unit} @ ${formatCurrency(pricePerUnit, '')}/${unit})\n`
        }

        // Modifiers for this item
        if (item.orderDetailModifiers && item.orderDetailModifiers.length > 0) {
            item.orderDetailModifiers.forEach(mod => {
                const modLabel = mod.modifierGroupItem?.modifierGroup?.name || ''
                const modOption = mod.modifierOptionName
                const prefix = mod.appliedModType?.toLowerCase() === 'remove' ? '- ' : '+ '
                const splitIndicator = mod.splitSide === 'left' ? '[L] ' : mod.splitSide === 'right' ? '[R] ' : ''
                const displayText = modLabel ? `${modLabel}: ${modOption}` : modOption
                let modName = `${prefix}${splitIndicator}${displayText}`

                if (mod.priceAdjustment !== 0 && mod.priceAdjustment !== null) {
                    const modPriceStr = formatCurrency(mod.priceAdjustment, '').padStart(7)
                    modName = modName.substring(0, 32).padEnd(32)
                    content += `      ${modName} ${modPriceStr}\n`
                } else {
                    content += `      ${modName.substring(0, 40)}\n`
                }
            })
        }

        // Item notes (special instructions)
        if (item.notes) {
            content += `      Note: ${item.notes.substring(0, 40)}\n`
        }
    })

    content += createLineText()

    // Totals Section
    content += `${'Subtotal:'.padStart(40)} ${formatCurrency(order.subtotal)}\n`

    // Deal discounts
    const textAppliedDeals = order.appliedDeals || order.applied_deals || []
    const textDealsArray = typeof textAppliedDeals === 'string' ? JSON.parse(textAppliedDeals) : textAppliedDeals
    let textTotalDealDiscount = 0

    if (textDealsArray && textDealsArray.length > 0) {
        textDealsArray.forEach(deal => {
            if (deal.amount > 0) {
                const dealName = deal.name || deal.dealName || 'Deal'
                content += `${(dealName + ':').padStart(40)} -${formatCurrency(deal.amount)}\n`
                textTotalDealDiscount += Number(deal.amount)
            }
        })
    }

    // Manual discount (only show if there's additional discount beyond deal discount)
    const textTotalDiscountAmount = Number(order.discountAmount || order.discount_amount || order.discount || 0)
    const textManualDiscount = textTotalDiscountAmount - textTotalDealDiscount
    if (textManualDiscount > 0.01) {
        content += `${'Discount:'.padStart(40)} -${formatCurrency(textManualDiscount)}\n`
    }

    const textVoucherCode = order.appliedVoucherCode || order.applied_voucher_code
    const textVoucherDiscount = order.voucherDiscount || order.voucher_discount || 0
    if (textVoucherCode || textVoucherDiscount > 0) {
        if (textVoucherCode) {
            content += `${'Voucher:'.padStart(40)} ${textVoucherCode}\n`
        }
        if (textVoucherDiscount > 0) {
            content += `${'Voucher Discount:'.padStart(40)} -${formatCurrency(textVoucherDiscount)}\n`
        }
    }

    if (order.loyaltyRewardName || order.loyalty_reward_name) {
        const rewardName = order.loyaltyRewardName || order.loyalty_reward_name
        const rewardType = order.loyaltyRewardType || order.loyalty_reward_type
        const rewardValue = order.loyaltyRewardValue || order.loyalty_reward_value
        const pointsRedeemed = order.loyaltyPointsRedeemed || order.loyalty_points_redeemed
        const discountAmount = order.loyaltyRewardDiscount || order.loyalty_reward_discount || order.loyalty_discount_applied || 0

        // Format reward description with value
        let rewardDescription = rewardName
        if (rewardType === 'discount_percentage' && rewardValue) {
            rewardDescription += ` (${rewardValue}% off)`
        } else if (rewardType === 'discount_fixed' && rewardValue) {
            rewardDescription += ` ($${rewardValue} off)`
        }

        content += `${'Loyalty Reward:'.padStart(40)} ${rewardDescription}\n`
        if (discountAmount > 0) {
            content += `${'Reward Discount:'.padStart(40)} -${formatCurrency(discountAmount)}\n`
        }
        if (pointsRedeemed) {
            content += `${'Points Redeemed:'.padStart(40)} ${pointsRedeemed}\n`
        }
    }

    const textGiftCardAmount = order.giftCardAmount || order.gift_card_amount || 0
    const textGiftCardCode = order.giftCardCode || order.gift_card_code
    if (textGiftCardAmount > 0 || textGiftCardCode) {
        if (textGiftCardCode) {
            content += `${'Gift Card:'.padStart(40)} ${textGiftCardCode}\n`
        }
        if (textGiftCardAmount > 0) {
            content += `${'Gift Card Applied:'.padStart(40)} -${formatCurrency(textGiftCardAmount)}\n`
        }
    }

    if (order.deliveryFee > 0) {
        content += `${'Delivery Fee:'.padStart(40)} ${formatCurrency(order.deliveryFee)}\n`
    }

    if (order.serviceFee > 0) {
        content += `${'Service Fee:'.padStart(40)} ${formatCurrency(order.serviceFee)}\n`
    }

    if (order.paymentSurcharge > 0) {
        content += `${'Payment Surcharge:'.padStart(40)} ${formatCurrency(order.paymentSurcharge)}\n`
    }

    if (order.orderSurcharge > 0) {
        content += `${'Order Surcharge:'.padStart(40)} ${formatCurrency(order.orderSurcharge)}\n`
    }

    const tipAmount = order.tipAmount || order.tip_amount || order.tip || 0
    if (tipAmount > 0) {
        content += `${'Tip:'.padStart(40)} ${formatCurrency(tipAmount)}\n`
    }

    // Platform Fee Disclosure (Transparency)
    if (order.platformFeeApplied > 0) {
        content += `${'Platform Fee:'.padStart(40)} ${formatCurrency(order.platformFeeApplied)}\n`
    }

    content += createLineText('-')

    // GST Breakdown (MANDATORY for tax compliance)
    const subtotalBeforeGst = order.subtotal / gstDivisor
    const taxAmount = order.taxAmount || order.tax_amount || 0
    const gstAmount = taxAmount || (order.subtotal - subtotalBeforeGst)

    if (isGSTRegistered && gstAmount > 0) {
        content += `${'Subtotal (Ex GST):'.padStart(40)} ${formatCurrency(subtotalBeforeGst)}\n`
        content += `${`GST (${gstPercentage}%):`.padStart(40)} ${formatCurrency(gstAmount)}\n`
    } else if (order.taxAmount > 0) {
        content += `${'GST:'.padStart(40)} ${formatCurrency(order.taxAmount)}\n`
    }

    content += createLineText('=')
    const totalAmount = order.totalAmount || order.total_amount || order.total || 0
    content += `${'TOTAL:'.padStart(40)} ${formatCurrency(totalAmount)}\n`
    content += createLineText('=')

    // Payment Method Details - check both top level and paymentInfo
    const paymentInfoText = order.paymentInfo || {}
    const cardTypeText = order.cardType || paymentInfoText.cardType
    const last4DigitsText = order.last4Digits || paymentInfoText.last4Digits
    const terminalTransactionIdText = order.terminalTransactionId || paymentInfoText.terminalTransactionId
    const terminalAuthCodeText = order.terminalAuthCode || paymentInfoText.terminalAuthCode
    const terminalAidText = order.terminalAid || paymentInfoText.terminalAid
    const terminalProviderText = order.terminalProvider || paymentInfoText.terminalProvider

    if (order.paymentMethodType) {
        content += '\n'
        content += 'PAYMENT DETAILS\n'
        content += createLineText('-')

        const paymentType = order.paymentMethodType?.toUpperCase() || 'CARD'
        content += `Method: ${paymentType}\n`

        if (cardTypeText) {
            content += `Card: ${cardTypeText}`
            if (last4DigitsText) {
                content += ` ****${last4DigitsText}`
            }
            content += '\n'
        } else if (last4DigitsText) {
            content += `Card: ****${last4DigitsText}\n`
        }

        if (terminalTransactionIdText) {
            content += `Ref: ${terminalTransactionIdText}\n`
        }

        if (terminalAuthCodeText) {
            content += `Auth: ${terminalAuthCodeText}\n`
        }

        if (terminalAidText) {
            content += `AID: ${terminalAidText}\n`
        }

        if (terminalProviderText) {
            content += `Terminal: ${terminalProviderText.toUpperCase()}\n`
        }

        content += createLineText('-')
    }

    // Customer Information (for tax invoice requirements over $1000)
    if (isGSTRegistered && order.totalAmount >= 1000) {
        content += '\n'
        content += 'CUSTOMER DETAILS:\n'
        if (order.customerFirstName || order.customerLastName) {
            content += `${order.customerFirstName || ''} ${order.customerLastName || ''}`.trim() + '\n'
        }
        if (order.customerEmail) {
            content += `${order.customerEmail}\n`
        }
        if (order.customerPhone) {
            content += `${order.customerPhone}\n`
        }
    }

    content += '\n'

    // Footer
    content += 'Thank You for Your Business!'.padStart(35) + '\n'

    // Return Policy (Consumer Rights)
    content += '\n'
    content += 'Voluntary tips does not attract GST\n'
    content += 'Goods presented must be as described,\n'
    content += 'fit for purpose and of acceptable quality\n'

    // Custom footer text (from business settings)
    if (order.business?.receiptFooterText) {
        content += '\n'
        content += createLineText('-')
        content += order.business.receiptFooterText + '\n'
    }

    // QR Code indication (from business settings)
    if (order.business?.receiptShowQrCode && order.business?.receiptQrCodeUrl) {
        content += '\n'
        content += createLineText('-')
        if (order.business.receiptQrCodeLabel) {
            content += order.business.receiptQrCodeLabel + '\n'
        }
        content += '[QR Code: ' + order.business.receiptQrCodeUrl + ']\n'
    }

    return content
}

/**
 * Generates a text-only version of the kitchen receipt for preview purposes.
 * @param {object} order - The full order object from your backend.
 * @returns {string} Plain text kitchen receipt content.
 */
export function generateKitchenReceiptText(order) {
    let content = ''

    // Priority Banner
    const priority = order.priority || 'normal'
    if (priority === 'rush') {
        content += '!!! RUSH ORDER !!!\n'.padStart(35)
        content += '\n'
    } else if (priority === 'urgent') {
        content += '** URGENT **\n'.padStart(32)
        content += '\n'
    }

    // Header
    content += 'KITCHEN ORDER'.padStart(30) + '\n'
    content += `ORDER #${order.id || order.backendOrderId || order.backend_order_id || order.orderNumber}`.padStart(28) + '\n'
    content += '\n'

    // Order info
    if (order.orderType) {
        content += `TYPE: ${order.orderType.toUpperCase()}\n`
    }

    const orderTime = new Date(order.placedAt || Date.now()).toLocaleTimeString()
    content += `TIME: ${orderTime}\n`

    // Customer name if available (for pickup/delivery)
    if (order.customerFirstName) {
        content += `CUSTOMER: ${order.customerFirstName} ${order.customerLastName || ''}\n`
    }

    // Table number if dine-in
    if (order.floorTableId) {
        content += `TABLE: ${order.floorTableId}\n`
    }

    content += createLineText('=')

    // Items to prepare
    content += 'ITEMS TO PREPARE:\n'
    content += createLineText()

    const itemsForText = order.orderDetails || []
    const hasCoursesText = itemsForText.some(item => item.courseId || item.course_id)

    if (hasCoursesText) {
        const courseGroupsText = groupItemsByCourse(itemsForText)
        let itemIndexText = 0

        courseGroupsText.forEach((group, groupIdx) => {
            if (group.courseName) {
                content += `=== ${group.courseName.toUpperCase()} ===\n`
            } else if (courseGroupsText.length > 1) {
                content += '--- No Course ---\n'
            }

            group.items.forEach((item) => {
                itemIndexText++
                content += printKitchenItemText(item, itemIndexText)
            })

            if (groupIdx < courseGroupsText.length - 1) {
                content += '\n'
            }
        })
    } else {
        itemsForText.forEach((item, index) => {
            content += printKitchenItemText(item, index + 1)
        })
    }

    // Special notes
    if (order.customerNotes) {
        content += createLineText('=')
        content += 'SPECIAL INSTRUCTIONS:\n'
        content += `${order.customerNotes}\n`
        content += '\n'
    }

    // Timing info
    content += createLineText('=')

    if (order.orderType === 'delivery' && order.scheduledAt) {
        content += `DELIVERY: ${new Date(order.scheduledAt).toLocaleTimeString()}`.padStart(32) + '\n'
    } else if (order.orderType === 'pickup' && order.scheduledAt) {
        content += `PICKUP: ${new Date(order.scheduledAt).toLocaleTimeString()}`.padStart(30) + '\n'
    } else if (order.orderType === 'dine_in') {
        content += 'DINE IN - SERVE HOT'.padStart(28) + '\n'
    }

    content += '\n'
    content += createLineText('=')
    content += `ORDER #${order.id || order.backendOrderId || order.backend_order_id || order.orderNumber}`.padStart(28) + '\n'
    content += `Printed: ${new Date().toLocaleTimeString()}`.padStart(32) + '\n'

    return content
}

// Helper to create a dashed line for text output
function createLineText(char = '-', length = 48) {
    return char.repeat(length) + '\n'
}

// Helper to print a single kitchen item in text format
function printKitchenItemText(item, index) {
    let content = ''
    const itemName = item.menuItemName || 'Unknown Item'
    const quantity = item.quantity || 1

    content += `${index}. ${quantity}x ${itemName}\n`

    if (item.isWeightBased || item.is_weight_based || item.weightSold || item.weight_sold) {
        const weight = item.weightSold || item.weight_sold
        const unit = item.weightUnit || item.weight_unit || 'kg'
        const pricePerUnit = item.pricePerUnit || item.price_per_unit || 0
        content += `   (${weight} ${unit} @ ${formatCurrency(pricePerUnit, '')}/${unit})\n`
    }

    if (item.orderDetailModifiers && item.orderDetailModifiers.length > 0) {
        item.orderDetailModifiers.forEach(mod => {
            const prefix = mod.appliedModType?.toLowerCase() === 'remove' ? '- ' : '+ '
            const splitIndicator = mod.splitSide === 'left' ? '[L] ' : mod.splitSide === 'right' ? '[R] ' : ''
            const displayText = mod.modifierGroupName ? `${mod.modifierGroupName}: ${mod.modifierOptionName}` : mod.modifierOptionName
            content += `   ${prefix}${splitIndicator}${displayText}\n`
        })
    }

    if (item.notes) {
        content += `   NOTE: ${item.notes}\n`
    }

    content += '\n'
    return content
}

// Helper to group items by course
function groupItemsByCourse(items) {
    if (!items || items.length === 0) return []

    const groups = {}
    const noCourseItems = []

    for (const item of items) {
        const courseId = item.courseId || item.course_id
        if (courseId) {
            if (!groups[courseId]) {
                groups[courseId] = {
                    courseId,
                    courseName: item.courseName || item.course_name || 'Course',
                    displayOrder: item.courseDisplayOrder || 999,
                    items: []
                }
            }
            groups[courseId].items.push(item)
        } else {
            noCourseItems.push(item)
        }
    }

    const sortedGroups = Object.values(groups).sort((a, b) => a.displayOrder - b.displayOrder)

    if (noCourseItems.length > 0) {
        sortedGroups.push({
            courseId: null,
            courseName: null,
            displayOrder: 9999,
            items: noCourseItems
        })
    }

    return sortedGroups
}
